// apps/web/src/layouts/components/WebPushToggle.tsx
//
// Toggle "Ativar notificações neste dispositivo" no rodapé do sininho.
// Some por completo quando o transporte não está disponível (navegador sem
// suporte OU VAPID off no backend) — NO-OP sem crash.
// Contrato: docs/contracts/ESCR-web-push-transport.md.

import { LoaderCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Switch } from '@alamo/ui';

import { useWebPushDevice } from './useWebPushDevice';

export function WebPushToggle() {
  const { t } = useTranslation(['common']);
  const { status, busy, operation, available, enable, disable, lastError } = useWebPushDevice();

  // Transporte indisponível → não renderiza nada (feature escondida).
  if (!available) return null;

  const isOn = status === 'subscribed';
  const isDenied = status === 'denied' || lastError === 'denied';

  async function handleToggle(next: boolean): Promise<void> {
    if (next) {
      const result = await enable();
      if (result === 'denied') {
        toast.error(t('common:notifications.push.denied'), { duration: 6000 });
        return;
      }
      if (result !== 'success') {
        toast.error(t(`common:notifications.push.${result}`));
        return;
      }
      toast.success(t('common:notifications.push.enabled'));
    } else {
      const result = await disable();
      if (result !== 'success') {
        toast.error(t(`common:notifications.push.${result}`));
        return;
      }
      toast.success(t('common:notifications.push.disabled'));
    }
  }

  const operationLabel =
    operation === 'enabling'
      ? t('common:notifications.push.enabling')
      : operation === 'disabling'
        ? t('common:notifications.push.disabling')
        : t('common:notifications.push.checking');

  return (
    <div
      className="flex items-center justify-between gap-3 border-t border-slate-200 p-4"
      data-testid="web-push-toggle"
      aria-busy={busy}
    >
      <div className="min-w-0">
        <p className="text-xs font-medium text-secondary-950">
          {t('common:notifications.push.toggle_label')}
        </p>
        <p className="text-xs text-slate-500">
          {busy
            ? operationLabel
            : isDenied
              ? t('common:notifications.push.denied')
              : t('common:notifications.push.toggle_hint')}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {busy ? (
          <LoaderCircle
            className="size-4 animate-spin text-laranja-encanto"
            aria-label={operationLabel}
            data-testid="web-push-loading"
          />
        ) : null}
        <Switch
          checked={isOn}
          disabled={busy || isDenied}
          onChange={(next) => {
            void handleToggle(next);
          }}
          aria-label={t('common:notifications.push.toggle_label')}
        />
      </div>
    </div>
  );
}
